import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../../core/shared/service/crud/mvs-crud.service";
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {OutletDto} from "../../../model/dto/entity/outlet.dto";
import {OutletBlogGhostWriterDto} from "../../../model/dto/entity/outlet-blog-ghost-writer.dto";
import {OutletService} from "../../../service/api/outlet.service";
import {Subject} from "rxjs";
import {distinctUntilChanged, finalize, takeUntil} from "rxjs/operators";
import {OutletBlogPostService} from "../../../service/api/outlet-blog-post.service";
import {OutletBlogPostDto} from "../../../model/dto/entity/outlet-blog-post.dto";
import {MessageService} from "primeng/api";
import {OutletBlogPostFromJsonRequestDto} from "../../../model/dto/entity/outlet-blog-post-from-json-request.dto";
import {ObjectRequestList} from "../../../../../core/shared/dto/object/request/object-request-list";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";

/**
 * Used
 */
@Component({
    selector: 'ol-create-blog-post',
    templateUrl: './create-blog-post.component.html',
    standalone: false,
})
export class CreateBlogPostComponent implements OnInit, OnChanges, OnDestroy {
    outletBlogGhostWriterService: MvsCrudService;
    busy = false;          // indicator whether the component is busy
    initialized = false;   // indicator whether the component was initialized

    blogPostForm: FormGroup;
    outletBlogPostDto: OutletBlogPostDto;
    outlets: OutletDto[] = [];
    ghostWriters: OutletBlogGhostWriterDto[] = [];

    promptText: string | null = null;
    promptGenerated = false;
    private readonly destroy$ = new Subject<void>();

    @Input() selectedOutletId: number | null = null;
    @Output() onBlogPostCreation: EventEmitter<OutletBlogPostDto> = new EventEmitter<OutletBlogPostDto>();

    constructor(
        protected coreService: MvsCoreService,
        
        private fb: FormBuilder,
        protected outletService: OutletService,
        protected outletBlogPostService: OutletBlogPostService,
        private messageService: MessageService
    ) {
        this.outletBlogGhostWriterService = this.coreService.getCrudService("ol.OutletBlogGhostWriter");

    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent() {
        this.refreshComponent();
    }

    /**
     * Refresh Component.
     */
    refreshComponent() {
        this.initForm();
        this.getOutlets();
        this.initialized = true;
    }

    initForm(): void {
        this.blogPostForm = this.fb.group({
            outletId: [{ value: null, disabled: true }, Validators.required],
            writerId: [{ value: null, disabled: true }, Validators.required],
            topic: ['', [Validators.required, Validators.minLength(3)]],
        });

        // react to outlet changes (avoid duplicates, avoid stale reads)
        this.outletId.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged()
            )
            .subscribe((outletId: number | null) => {
                // clear & disable writer until we load options for the new outlet
                this.ghostWriters = [];
                this.writerId.reset();
                this.writerId.disable({emitEvent: false});

                if (outletId != null) {
                    this.getGhostWriters(outletId);
                }
            });
    }

    // ---- Getters (typed) ----
    get outletId(): FormControl<number | null> {
        return this.blogPostForm.get('outletId') as FormControl<number | null>;
    }

    get writerId(): FormControl<number | null> {
        return this.blogPostForm.get('writerId') as FormControl<number | null>;
    }

    get topic(): FormControl<string> {
        return this.blogPostForm.get('topic') as FormControl<string>;
    }

    get json(): FormControl<string> {
        return this.blogPostForm.get('json') as FormControl<string>;
    }

    getOutlets(): void {
        this.outletService.get(this.selectedOutletId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (outlet: OutletDto) => {
                this.outlets = [outlet];

                // if parent provided a preselected outlet, apply it once
                if (this.selectedOutletId != null) {
                    const found = this.outlets.find(o => o.id === this.selectedOutletId);
                    if (found && this.outletId.value !== found.id) {
                        this.outletId.setValue(found.id);
                    }
                }
            }
        });
    }

    getGhostWriters(outletId: number): void {
        const request = ObjectRequestList.createBasic(
            false,
            [FilterCriteria.create('outlet', FilterCriteria.cOperatorEqual, outletId)],
            []
        );

        this.outletBlogGhostWriterService
            .list(request)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (gw) => {
                    this.ghostWriters = gw?.entries ?? [];
                    if (this.ghostWriters.length) {
                        this.writerId.enable({emitEvent: false});
                    }
                },
                error: () => {
                    this.ghostWriters = [];
                }
            });
    }

    submit(): void {
        if (!this.validateForm()) {
            return;
        }
        const {outletId, writerId, topic} = this.blogPostForm.getRawValue();

        this.outletBlogPostService
            .createBlogPost(outletId, writerId, topic)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.busy = false))
            )
            .subscribe({
                next: (outletBlogPostDto: OutletBlogPostDto) => {

                    this.resetForm();
                    this.outletBlogPostDto = outletBlogPostDto;
                    this.onBlogPostCreation.emit(this.outletBlogPostDto);
                }
            });
    }

    createPrompt(): void {
        if (!this.validateForm()) {
            return;
        }

        const {outletId, writerId, topic} = this.blogPostForm.getRawValue();

        this.outletBlogPostService
            .createOutletBlogPostPrompt(outletId, writerId, topic)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.busy = false))
            )
            .subscribe({
                next: (prompt: string) => {
                    this.promptText = prompt;   // store received string
                    this.promptGenerated = true;

                    // Dynamically add the 'json' control only now
                    if (!this.blogPostForm.contains('json')) {
                        this.blogPostForm.addControl(
                            'json',
                            new FormControl('', [Validators.required, Validators.minLength(3)])
                        );
                    }
                },
                error: () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Prompt Generation Failed',
                        detail: 'An unexpected error occurred. Please try again.'
                    });
                    this.promptGenerated = false;
                }
            });
    }

    copyPromptToClipboard(): void {
        if (!this.promptText) return;

        navigator.clipboard.writeText(this.promptText).then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Copied to Clipboard',
                detail: 'Prompt text has been copied successfully.'
            });
        });
    }

    private validateForm(controlNames?: string[]): boolean {
        const controls = controlNames ?? Object.keys(this.blogPostForm.controls);
        controls.forEach(name => this.blogPostForm.get(name)?.markAsTouched());
        if (this.blogPostForm.invalid || this.busy) {
            return false;
        }
        this.busy = true;
        return true;
    }

    private resetForm(): void {
        this.blogPostForm.reset({
            outletId: null,
            writerId: null,
            topic: ''
        });
    }


    createFromJson() {
        if (!this.validateForm()) {
            return;
        }

        const {outletId, writerId, json} = this.blogPostForm.getRawValue();

        const outletBlogPostFromJson: OutletBlogPostFromJsonRequestDto = {
            outletId,
            ghostWriterId: writerId,
            json
        };

        this.outletBlogPostService
            .createOutletBlogPostFromJson(outletId, outletBlogPostFromJson)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.busy = false))
            )
            .subscribe({
                next: (outletBlogPostDto: OutletBlogPostDto) => {
                    this.resetForm();

                    this.outletBlogPostDto = outletBlogPostDto;
                    this.onBlogPostCreation.emit(this.outletBlogPostDto);
                }
            });
    }

    /**
     * Process changes within Binding.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) return;

        if (changes['selectedOutletId']) {
            const next: number | null = changes['selectedOutletId'].currentValue;
            if (next != null && this.outletId.value !== next) {
                this.outletId.setValue(next);
            }
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
